/**
 * Signup acquisition attribution (frontend).
 *
 * The app domain (app.xpherium.com) has no web analytics, so a plain UTM link
 * pointing straight at signup records nothing. This captures the campaign tags
 * at the FIRST app touch, persists them (localStorage + a 90-day cookie so they
 * survive the create-account journey, refresh, and tab close), and forwards
 * them with the register payload. The backend stamps them onto the new account
 * so we can later answer "which campaign drove this signup — and did it convert
 * to paid?".
 *
 * This is independent of the affiliate `?ref=` system (see referralAttribution
 * .js): no commission, no referral banner — purely first-party acquisition
 * analytics for marketing campaigns (e.g. a sponsored creator post).
 *
 * Lifecycle:
 *   1. Visitor lands with `?utm_source=…&utm_campaign=…` (and friends).
 *   2. We persist the bag (first-touch wins — the campaign that *brought* them
 *      is the one that gets credit, even if they bounce around before signing
 *      up).
 *   3. At signup the register payload includes `signupAttribution`.
 *   4. After a successful signup we clear it so a later, unrelated signup in the
 *      same browser isn't mis-attributed.
 */

const STORAGE_KEY = 'xph_signup_attribution';
const COOKIE_NAME = 'xph_attr';
const COOKIE_DAYS = 90;

// The UTM params we capture. Mirrors the GA4 standard set.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Defensive caps so a hand-crafted URL can't bloat what we store/send.
const MAX_LEN = 256;

function safeWindow() {
  return typeof window !== 'undefined' ? window : null;
}

function clean(v) {
  if (v == null) return null;
  const s = String(v).trim().slice(0, MAX_LEN);
  return s || null;
}

function readCookie(name) {
  const w = safeWindow();
  if (!w?.document?.cookie) return null;
  for (const c of w.document.cookie.split(';').map((x) => x.trim())) {
    if (c.startsWith(`${name}=`)) {
      try { return decodeURIComponent(c.slice(name.length + 1)); } catch { return null; }
    }
  }
  return null;
}

function writeCookie(name, value, days = COOKIE_DAYS) {
  const w = safeWindow();
  if (!w?.document) return;
  const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString();
  w.document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function clearCookie(name) {
  const w = safeWindow();
  if (!w?.document) return;
  w.document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function parseStored(raw) {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : null;
  } catch {
    return null;
  }
}

/** Read the persisted attribution bag (localStorage first, cookie fallback). */
export function getStoredAttribution() {
  try {
    const fromStorage = parseStored(localStorage.getItem(STORAGE_KEY));
    if (fromStorage) return fromStorage;
  } catch { /* ignore */ }
  return parseStored(readCookie(COOKIE_NAME));
}

function persist(bag) {
  const raw = JSON.stringify(bag);
  try { localStorage.setItem(STORAGE_KEY, raw); } catch { /* ignore */ }
  writeCookie(COOKIE_NAME, raw);
}

/**
 * Capture `utm_*` from the current URL on first app touch. First-touch wins:
 * if we already have a stored campaign, we keep it (so navigation within the
 * app doesn't blank out the campaign that actually brought the visitor).
 * Idempotent — safe to call on every mount.
 */
export function captureAttributionFromUrl() {
  const w = safeWindow();
  if (!w?.location) return getStoredAttribution();

  // Already captured this visitor — don't overwrite the original campaign.
  const existing = getStoredAttribution();
  if (existing && (existing.utm_source || existing.utm_campaign)) return existing;

  let params;
  try {
    params = new URL(w.location.href).searchParams;
  } catch {
    return existing;
  }

  const bag = {};
  for (const key of UTM_KEYS) {
    const val = clean(params.get(key));
    if (val) bag[key] = val;
  }

  // Nothing to capture — don't write an empty/garbage record.
  if (!bag.utm_source && !bag.utm_campaign) return existing;

  bag.referrer = clean(w.document?.referrer) || null;
  bag.landing_path = clean(w.location.pathname) || '/';
  bag.captured_at = new Date().toISOString();

  persist(bag);
  return bag;
}

/**
 * Partial register-payload contribution. Returns {} when there's no campaign in
 * play, so callers can spread it unconditionally without polluting organic
 * signups.
 */
export function getSignupAttributionPayload() {
  const bag = getStoredAttribution();
  if (!bag || (!bag.utm_source && !bag.utm_campaign)) return {};
  return { signupAttribution: bag };
}

/** Clear persisted attribution (call after a successful signup). */
export function clearStoredAttribution() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  clearCookie(COOKIE_NAME);
}

const signupAttribution = {
  captureAttributionFromUrl,
  getStoredAttribution,
  getSignupAttributionPayload,
  clearStoredAttribution,
};

export default signupAttribution;
